import {
  Component,
  ChangeDetectionStrategy,
  signal,
  input,
  effect,
  output,
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';
import { ChipComponent } from "src/app/ui-kit/chip/chip.component";

/**
 * Reusable filter group component with collapsible content.
 * 
 * Provides a consistent UI for filter groups with a title, toggle button,
 * and animated expand/collapse behavior. Uses ng-content projection for
 * flexible filter content.
 * 
 * @example
 * ```html
 * <app-filter-group #filterGroup title="Grade">
 *   <app-checkbox-input [checked]="true">Grade 1</app-checkbox-input>
 *   <app-checkbox-input [checked]="false">Grade 2</app-checkbox-input>
 * </app-filter-group>
 * ```
 */
@Component({
  selector: 'app-filter-group',
  templateUrl: './filter-group.component.html',
  styleUrls: ['./filter-group.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, ChipComponent],
  host: {
    class: 'block',
  },
  animations: [
    trigger('expandCollapse', [
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
          overflow: 'visible',
        })
      ),
      state(
        'collapsed',
        style({
          height: '0',
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      transition('expanded <=> collapsed', [animate('250ms ease-out')]),
    ]),
  ],
})
export class FilterGroupComponent {
  /** Animation duration matching card-accordion-container */
  static readonly ANIMATION_DURATION = '250ms ease-out';

  /**
   * Title displayed in the filter group header
   */
  title = input.required<string>();

  /**
   * Controls the initial/reactive collapsed state of the filter group.
   * When this value changes, the filter group will update its state accordingly.
   */
  initialCollapsed = input<boolean>(false);

  /**
   * Controls the active filter count of the filter group.
   * When this value changes, the filter group will update its state accordingly.
   */
  activeFilterCount = input<number>(0);

  /**
   * Internal signal tracking the current collapsed state.
   */
  collapsed = signal<boolean>(false);

  /**
   * Clears the filters of the filter group.
   */
  onClearFilters = output<void>();

  constructor() {
    // React to changes in initialCollapsed input
    effect(() => {
      this.collapsed.set(this.initialCollapsed());
    });
  }

  /**
   * Toggles the collapsed state of the filter group.
   * 
   * @example
   * ```html
   * <app-filter-group #filterGroup title="Grade">
   *   <!-- content -->
   * </app-filter-group>
   * <button (click)="filterGroup.toggleCollapsed()">Toggle</button>
   * ```
   */
  toggleCollapsed = () => {
    this.collapsed.update((value) => !value);
  };
}

